// src/components/templates/TemplateHeader.jsx
import React, { useMemo, useState } from "react";
import {
    Paper,
    Stack,
    TextField,
    Button,
    Typography,
    Chip,
    InputAdornment,
    Divider,
    ToggleButtonGroup,
    ToggleButton,
    IconButton,
    Tooltip,
    Box,
    Menu,
    MenuItem,
    useTheme,
} from "@mui/material";

import * as Lucide from "lucide-react";

export default function TemplateHeader({
                                           value = "",
                                           onChange,
                                           onClear,

                                           onNew,
                                           onNewDoc,

                                           status = "all",
                                           onStatusChange,
                                           total,
                                           pendingCount,
                                           completeCount,

                                           sort = "recent",
                                           onSortChange,

                                           density = "comfortable",
                                           onDensityChange,
                                       }) {
    const theme = useTheme();
    const [sortAnchor, setSortAnchor] = useState(null);

    const sortLabel = useMemo(() => {
        switch (sort) {
            case "name": return "Nome (A–Z)";
            case "updated": return "Atualizados";
            default: return "Mais recentes";
        }
    }, [sort]);

    const handleOpenSort = (e) => setSortAnchor(e.currentTarget);
    const handleCloseSort = () => setSortAnchor(null);
    const chooseSort = (key) => {
        onSortChange?.(key);
        handleCloseSort();
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 1.5, sm: 2 },
                mb: 2,
                borderRadius: 3,
                background: (t) =>
                    t.palette.mode === "light"
                        ? "linear-gradient(180deg, #fff 0%, #fafafa 100%)"
                        : "linear-gradient(180deg, #161B22 0%, #0D1117 100%)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                borderColor: (t) => (t.palette.mode === "light" ? "#eee" : "rgba(48,54,61,0.8)"),
            }}
        >
            <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", lg: "center" }}
                justifyContent="space-between"
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", md: "center" }}
                    sx={{ flex: 1 }}
                >
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 180 }}>
                        <Lucide.FileText color={theme.palette.primary.main} />
                        <Typography variant="h5" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                            Templates
                        </Typography>
                        {typeof total === "number" && (
                            <Chip size="small" label={total} sx={{ fontWeight: 600, ml: 0.25 }} />
                        )}
                    </Stack>

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar por nome, tag ou versão…"
                        value={value}
                        onChange={(e) => onChange?.(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lucide.Search size={18} />
                                </InputAdornment>
                            ),
                            endAdornment: value ? (
                                <InputAdornment position="end">
                                    <Tooltip title="Limpar">
                                        <IconButton size="small" onClick={() => (onClear ? onClear() : onChange?.(""))}>
                                            <Lucide.X size={18} />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ) : null,
                            sx: { borderRadius: 999 },
                        }}
                    />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: { xs: 0.5, lg: 0 } }}>
                    <Tooltip title={`Ordenar por: ${sortLabel}`}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Lucide.ArrowDownUp size={18} />}
                            onClick={handleOpenSort}
                            sx={{ textTransform: "none", borderRadius: 999 }}
                        >
                            {sortLabel}
                        </Button>
                    </Tooltip>

                    <Tooltip title={density === "compact" ? "Densidade: compacta" : "Densidade: confortável"}>
                        <IconButton
                            onClick={() => onDensityChange?.(density === "compact" ? "comfortable" : "compact")}
                            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                            size="small"
                        >
                            {density === "compact" ? <Lucide.Columns3 size={18} /> : <Lucide.Rows3 size={18} />}
                        </IconButton>
                    </Tooltip>

                    {onNewDoc && (
                        <Button
                            variant="outlined"
                            onClick={onNewDoc}
                            disableElevation
                            sx={{ borderRadius: 999, textTransform: "none", whiteSpace: "nowrap" }}
                        >
                            Novo documento
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        onClick={onNew}
                        startIcon={<Lucide.Plus size={18} />}
                        disableElevation
                        sx={{
                            ml: 0.5,
                            borderRadius: 999,
                            px: 2.2,
                            whiteSpace: "nowrap",
                            background: (t) => `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
                            boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        }}
                    >
                        Novo template
                    </Button>
                </Stack>
            </Stack>

            <Box
                sx={{
                    mt: 1.5,
                    p: 1,
                    borderRadius: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    background: theme.palette.mode === "light" ? "#fff" : "rgba(255,255,255,0.02)",
                }}
            >
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }}>
                    <ToggleButtonGroup
                        size="small"
                        color="primary"
                        exclusive
                        value={status}
                        onChange={(_, v) => v && onStatusChange?.(v)}
                        sx={{
                            borderRadius: 999,
                            "& .MuiToggleButton-root": { px: 1.5, textTransform: "none" },
                            flexWrap: "wrap",
                        }}
                    >
                        <ToggleButton value="all">Todos</ToggleButton>
                        <ToggleButton value="pending">
                            Pendentes {typeof pendingCount === "number" && <Chip size="small" label={pendingCount} sx={{ ml: 0.75 }} />}
                        </ToggleButton>
                        <ToggleButton value="complete">
                            Completos {typeof completeCount === "number" && <Chip size="small" label={completeCount} sx={{ ml: 0.75 }} />}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "block" } }} />

                    <Tooltip title="Mais filtros (em breve)">
            <span>
              <Button
                  size="small"
                  variant="text"
                  startIcon={<Lucide.SlidersHorizontal size={18} />}
                  disabled
                  sx={{ textTransform: "none" }}
              >
                Avançado
              </Button>
            </span>
                    </Tooltip>
                </Stack>
            </Box>

            <Menu
                anchorEl={sortAnchor}
                open={Boolean(sortAnchor)}
                onClose={handleCloseSort}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem selected={sort === "recent"} onClick={() => chooseSort("recent")}>
                    Mais recentes
                </MenuItem>
                <MenuItem selected={sort === "updated"} onClick={() => chooseSort("updated")}>
                    Atualizados
                </MenuItem>
                <MenuItem selected={sort === "name"} onClick={() => chooseSort("name")}>
                    Nome (A–Z)
                </MenuItem>
            </Menu>
        </Paper>
    );
}
